import type { ContactInput, ApiSuccess, ApiError } from '@hun-tech/shared/schemas'

export class ApiErrorResponse extends Error {
  constructor(
    public readonly status: number,
    public readonly errorData: ApiError
  ) {
    super(errorData.error || `Request failed with status ${status}`)
    this.name = 'ApiErrorResponse'
  }
}

const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL
  return (envUrl && envUrl.trim().length > 0 ? envUrl : '/api').replace(/\/+$/, '')
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const baseUrl = getApiBaseUrl()
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  const url = `${baseUrl}${path}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers as Record<string, string>),
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  let data: unknown = null
  try {
    data = await response.json()
  } catch {
    // Non-JSON response body
  }

  if (!response.ok) {
    const errorData: ApiError =
      data && typeof data === 'object' && 'error' in data
        ? (data as ApiError)
        : {
            success: false,
            error:
              (data &&
              typeof data === 'object' &&
              'message' in data &&
              typeof (data as { message: unknown }).message === 'string'
                ? (data as { message: string }).message
                : response.statusText) || 'Request failed',
          }

    throw new ApiErrorResponse(response.status, errorData)
  }

  return data as T
}

/**
 * Direct browser transmission to Formspree endpoint.
 * Calling Formspree directly from the client ensures real residential/mobile IP
 * and browser TLS fingerprints, preventing datacenter bot/spam filtering.
 */
async function postToFormspree(data: ContactInput): Promise<boolean> {
  const formspreeEndpoint =
    import.meta.env.VITE_FORMSPREE_ENDPOINT || 'https://formspree.io/f/xrpbayvk'

  if (!formspreeEndpoint || formspreeEndpoint.trim().length === 0) {
    return false
  }

  const endpoint = formspreeEndpoint.trim()
  const url = endpoint.startsWith('http')
    ? endpoint
    : `https://formspree.io/f/${endpoint}`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        subject: data.subject,
        message: data.message,
        _replyto: data.email,
        _subject: `New Project Inquiry: ${data.subject} (${data.name})`,
      }),
    })

    return response.ok
  } catch (err) {
    console.warn('[Formspree Direct Client Dispatch Skipped/Failed]', err)
    return false
  }
}

/**
 * Sends contact inquiry via dual-dispatch:
 * 1. Directly to Formspree from the client's browser (triggers instant email alert to company inbox).
 * 2. Directly to HUN-TECH API -> Cloudflare D1 SQL (guarantees permanent database persistence & lead capture).
 */
export const postContact = async (data: ContactInput): Promise<ApiSuccess> => {
  // Fire both in parallel for optimal responsiveness and guaranteed delivery
  const [formspreeResult, apiResult] = await Promise.allSettled([
    postToFormspree(data),
    request<ApiSuccess>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  ])

  const formspreeSucceeded =
    formspreeResult.status === 'fulfilled' && formspreeResult.value === true
  const apiSucceeded = apiResult.status === 'fulfilled'

  // If at least one channel captured the inquiry, treat as successful
  if (apiSucceeded) {
    return apiResult.value
  }

  if (formspreeSucceeded) {
    return {
      success: true,
      message:
        'Message received! Our engineering leadership will respond within 24 hours.',
    }
  }

  // If both failed, bubble up the API error or fallback to a general error
  if (apiResult.status === 'rejected') {
    throw apiResult.reason
  }

  throw new Error('Failed to deliver transmission. Please try again shortly.')
}

export const api = {
  postContact,
}

export const apiClient = api

export default api
